import { FC, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Divider } from '@mui/material';

interface YearMonthPickerProps {
	onChange?: (date: { year: number; month: number }) => void;
	initialYear?: number | null;
	initialMonth?: number | null;
	onClose?: () => void;
	filterSelector?: string;
	numberOfYears?: number;
}

export const YearMonthPiker: FC<YearMonthPickerProps> = ({
	onChange,
	initialYear = null,
	initialMonth = null,
	onClose,
	filterSelector,
	numberOfYears = 12,
}) => {
	const currentYear = new Date().getFullYear();
	const currentMonth = new Date().getMonth() + 1;

	// Валидация и установка начальных значений
	let validatedInitialYear: number | null = null;
	if (initialYear !== null) {
		validatedInitialYear = Math.min(initialYear, currentYear);
	}

	let validatedInitialMonth: number | null = null;
	if (validatedInitialYear !== null && initialMonth !== null) {
		if (validatedInitialYear === currentYear) {
			validatedInitialMonth = Math.min(initialMonth, currentMonth);
		} else {
			validatedInitialMonth = initialMonth;
		}
	}

	const [searchParams, setSearchParams] = useSearchParams();

	const [selectedYear, setSelectedYear] = useState<number | null>(validatedInitialYear);
	const [selectedMonth, setSelectedMonth] = useState<number | null>(validatedInitialMonth);
	// Показываем месяцы сразу, если год был передан
	const [showMonthGrid, setShowMonthGrid] = useState<boolean>(validatedInitialYear !== null);

	const handleYearClick = (year: number) => {
		setSelectedYear(year);
		setSelectedMonth(null); // Сбрасываем месяц при выборе нового года
		setShowMonthGrid(true); // Показываем сетку месяцев
		// onChange не вызываем, ждем выбора месяца
	};

	const handleMonthClick = (month: number) => {
		// Запрещаем клик по будущим месяцам в текущем году
		if (selectedYear === currentYear && month > currentMonth) {
			return;
		}
		setSelectedMonth(month);
		if (selectedYear !== null) {
			onChange?.({ year: selectedYear, month });
			if (filterSelector) {
				setSearchParams((prevParams) => {
					const newSearchParams = new URLSearchParams(prevParams);
					newSearchParams.set(
						filterSelector,
						`${selectedYear}-${month < 10 ? `0${month}` : month}`,
					);
					return newSearchParams;
				});
			}
		}
		onClose?.();
	};

	// Генерируем последние 12 лет, включая текущий
	const years = Array.from({ length: numberOfYears }, (_, i) => currentYear - i).reverse();
	const monthNames = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	];

	const gridStyle: React.CSSProperties = {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(77px, 1fr))',
		gap: '8px',
		// marginBottom: '15px',
		textAlign: 'center',
		padding: '5px',
	};

	const itemStyle: React.CSSProperties = {
		padding: '4px',
		// border: '1px solid #ccc',
		cursor: 'pointer',
		color: '#006293',
		borderRadius: '12px',
	};

	const selectedItemStyle: React.CSSProperties = {
		...itemStyle,
		backgroundColor: '#006293',
		color: 'white',
		fontWeight: 'bold',
	};

	const disabledItemStyle: React.CSSProperties = {
		...itemStyle,
		// backgroundColor: '#e9ecef',
		color: '#00629360',
		cursor: 'not-allowed',
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
			{/* Year Selection Grid */}
			<Box sx={gridStyle}>
				{years.map((y) => (
					<div
						key={y}
						style={y === selectedYear ? selectedItemStyle : itemStyle}
						onClick={() => handleYearClick(y)}
					>
						{y}
					</div>
				))}
			</Box>

			{/* Month Selection Grid (conditional) */}
			{showMonthGrid && (
				<>
					<Divider sx={{ borderColor: 'primary.light', borderWidth: '1.2px' }} />
					<Box sx={gridStyle}>
						{monthNames.map((name, index) => {
							const monthNumber = index + 1;
							// Проверяем, является ли месяц будущим только если год выбран
							const isFutureMonth = selectedYear === currentYear && monthNumber > currentMonth;

							let style = itemStyle;
							if (isFutureMonth) {
								style = disabledItemStyle;
							} else if (selectedYear !== null && monthNumber === selectedMonth) {
								// Выделяем месяц только если он выбран
								style = selectedItemStyle;
							}

							return (
								<div key={name} style={style} onClick={() => handleMonthClick(monthNumber)}>
									{name}
								</div>
							);
						})}
					</Box>
				</>
			)}
		</Box>
	);
};
